/* eslint-disable react/no-children-prop */
import React from 'react';
import Image from 'next/image';
import cn from 'classnames';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import Section from 'react-scale/components/ui/section';

import styles from './article.module.css';
import PrismHighlighter from '../prism-highlighter';

dayjs.extend(advancedFormat);

const Article = ({ post, content }) => {
  if (!post) {
    return null;
  }

  const coverImage = post.coverImageInput ?? post.coverImage;

  return (
    <Section relative paddingBottom={false} paddingTop={false}>
      <article className="py-10 lg:pt-7 lg:pb-24">
        <header className="flex flex-col justify-center max-w-5xl mx-auto text-center">
          {post.category?.value?.data?.name && (
            <h4 className="mx-auto mb-4 lg:mb-8 text-overtitle-sm text-transform-gradient max-w-max">
              {post.category.value.data.name}
            </h4>
          )}
          <h1 className="text-4xl font-normal lg:text-5xl font-aeonik">{post.title}</h1>
          {post.authors && (
            <p className="mt-6 text-base text-gray-400 md:text-xl">
              {post.authors.length > 0 && 'by '}
              {post.authors.map((author, i) => {
                let join = '';
                if (post.authors.length > 1 && i < post.authors.length - 1) {
                  if (i === post.authors.length - 2) {
                    join = ' and ';
                  } else {
                    join = ', ';
                  }
                }
                return (
                  <span key={author.author.value?.data?.name}>
                    <a
                      className={cn({
                        'text-scale-mauve': i % 2 === 0,
                        'text-scale-lightskyblue': i % 2 !== 0
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={
                        author.author.value?.data?.linkedin ??
                        author.author.value?.data?.twitter ??
                        author.author.value?.data?.alternativeLink
                      }
                    >
                      {author.author.value?.data?.name}
                    </a>
                    {join}
                  </span>
                );
              })}
              <span> on </span>
              {post.date && <span>{dayjs(post.date).format('MMMM Do, YYYY')}</span>}
            </p>
          )}
          {coverImage && (
            <figure className="mx-0 my-12 overflow-hidden shadow-2xl rounded-xl md:mt-16 md:mb-20">
              <Image
                layout="responsive"
                quality={100}
                loading="eager"
                priority
                src={coverImage}
                alt={`${post.title} cover`}
                sizes="(max-width: 767px) 500px, (max-width: 991px) 800px, 928px"
                width={928}
                height={464}
              />
            </figure>
          )}
        </header>
        <div className={`${styles.blogDetail} max-w-[800px] text-lg my-0 mx-auto py-0 px-1`}>
          <ReactMarkdown
            children={content}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, inline, children, className, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                if (!inline) {
                  return (
                    <PrismHighlighter
                      language={match}
                      children={String(children).replace(/\n$/, '')}
                      {...props}
                    />
                  );
                }
                return <code {...props}>{children}</code>;
              }
            }}
          />
        </div>
      </article>
    </Section>
  );
};

export default Article;
